import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {FileWidget} from '@core/components';
import {AttachmentService, SupplierService} from '@core/services';
import {ModalService} from '@core/services/modal.service';
import {CompanyStateService} from '@core/states';
import {Attachment, SupplierDetail, SupplierRow} from '@core/types';

@Component({
  selector: 'core-modal-add-supplier',
  imports: [
    FileWidget,
    ReactiveFormsModule
  ],
  templateUrl: './add-supplier.html',
  styleUrl: './add-supplier.scss',
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;'
  }
})
export class AddSupplier implements OnInit, OnDestroy {
  @Input() supplier: SupplierRow | SupplierDetail | null = null;
  form: FormGroup;
  isLoadingDetail = false;
  isSubmitting = false;
  logoFile: File | null = null;
  logoPreviewUrl = '';
  pendingAttachments: File[] = [];
  existingAttachments: Attachment[] = [];
  errorMessage = '';
  isEditMode = false;
  existingLogoFileName = '';
  private modal = inject(ModalService);
  private supplierService = inject(SupplierService);
  private attachmentService = inject(AttachmentService);
  private companyState = inject(CompanyStateService);
  private formBuilder = inject(FormBuilder);

  constructor() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      taxId: ['', [Validators.required]],
      phone: [''],
      email: ['', [Validators.email]],
      website: [''],
      contact: [''],
      basicNeed: ['no'],
      hasChannel: ['no'],
      compliance: ['no'],
      consolidated: ['no'],
    });
  }

  ngOnInit() {
    if (!this.supplier?.id) return;
    this.isEditMode = true;
    this.form.disable({emitEvent: false});
    this.loadSupplierDetail(this.supplier.id);
  }

  ngOnDestroy() {
    this.revokeLogoPreview();
  }

  async submit(closeAfter: boolean) {
    if (this.isSubmitting) return;
    if (this.isLoadingDetail) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.form.disable({emitEvent: false});
    const value = this.form.getRawValue();
    const response = this.isEditMode && this.supplier?.id
      ? await this.supplierService.updateSupplier(this.supplier.id, {
        name: value.name,
        taxId: value.taxId,
        phone: value.phone,
        email: value.email,
        website: value.website,
        contact: value.contact,
        metadata: {
          logoFileName: this.logoFile?.name || this.existingLogoFileName || undefined,
          basicNeed: value.basicNeed,
          hasChannel: value.hasChannel,
          compliance: value.compliance,
          consolidated: value.consolidated
        }
      }, {skipLoader: true})
      : await this.supplierService.createSupplier({
      name: value.name,
      taxId: value.taxId,
      phone: value.phone,
      email: value.email,
      website: value.website,
      contact: value.contact,
      metadata: {
        logoFileName: this.logoFile?.name || undefined,
        basicNeed: value.basicNeed,
        hasChannel: value.hasChannel,
        compliance: value.compliance,
        consolidated: value.consolidated
      }
      }, {skipLoader: true});

    if (!response.success) {
      this.isSubmitting = false;
      this.form.enable({emitEvent: false});
      this.errorMessage = response.message || (this.isEditMode ? 'No se pudo actualizar el proveedor.' : 'No se pudo crear el proveedor.');
      return;
    }

    const supplierId = response.result?.id || this.supplier?.id;
    const uploadResult = await this.uploadSelectedFiles(supplierId ?? null);
    if (!uploadResult.success) {
      this.isSubmitting = false;
      this.form.enable({emitEvent: false});
      this.errorMessage = uploadResult.message || 'El proveedor se guardó, pero no se pudieron subir los archivos.';
      return;
    }

    this.isSubmitting = false;
    this.form.enable({emitEvent: false});

    if (this.isEditMode || closeAfter) {
      this.modal.closeTop(true);
    } else {
      this.form.reset();
      this.clearSelectedFiles();
      this.form.patchValue({
        basicNeed: 'no',
        hasChannel: 'no',
        compliance: 'no',
        consolidated: 'no'
      });
    }
  }

  openLogoPicker() {
    if (this.isSubmitting || this.isLoadingDetail) return;
    const input = document.getElementById('supplier-logo-input') as HTMLInputElement | null;
    input?.click();
  }

  handleLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.logoFile = file;
    this.setLogoPreview(file);
    input.value = '';
  }

  openDocumentsPicker() {
    if (this.isSubmitting || this.isLoadingDetail) return;
    const input = document.getElementById('supplier-documents-input') as HTMLInputElement | null;
    input?.click();
  }

  handleDocumentsSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length) {
      this.pendingAttachments = [...this.pendingAttachments, ...files];
    }
    input.value = '';
  }

  removePendingAttachment(index: number) {
    this.pendingAttachments = this.pendingAttachments.filter((_, currentIndex) => currentIndex !== index);
  }

  formatFileSize(size?: number) {
    const bytes = Number(size || 0);
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(2)} KB`;
  }

  logoSrc() {
    return this.logoPreviewUrl || '';
  }

  private async loadSupplierDetail(supplierId: string | number) {
    this.isLoadingDetail = true;
    this.errorMessage = '';
    const response = await this.supplierService.getSupplierDetail(supplierId, {skipLoader: true});
    this.isLoadingDetail = false;

    if (!response.success || !response.result) {
      this.errorMessage = response.message || 'No se pudo cargar el proveedor.';
      this.form.enable({emitEvent: false});
      return;
    }

    const detail = response.result;
    this.form.patchValue({
      name: detail.name || '',
      taxId: detail.taxId || '',
      phone: detail.phone || '',
      email: detail.email || '',
      website: detail.website || '',
      contact: detail.contact || '',
      basicNeed: detail.metadata?.["basicNeed"] || 'no',
      hasChannel: detail.metadata?.["hasChannel"] || 'no',
      compliance: detail.metadata?.["compliance"] || 'no',
      consolidated: detail.metadata?.["consolidated"] || 'no'
    });
    this.existingLogoFileName = detail.metadata?.['logoFileName'] || '';

    if (!this.logoPreviewUrl) {
      await this.resolveExistingLogo();
    }

    await this.loadExistingAttachments(supplierId);

    this.form.enable({emitEvent: false});
  }

  get existingDocumentationAttachments() {
    if (!this.existingLogoFileName) {
      return this.existingAttachments;
    }

    return this.existingAttachments.filter(attachment => attachment.fileName !== this.existingLogoFileName);
  }

  async downloadExistingAttachment(attachment: Attachment) {
    if (!attachment?.id) return;

    const response = await this.attachmentService.downloadAttachment(attachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.errorMessage = response.message || 'No se pudo descargar el adjunto.';
      return;
    }

    const url = URL.createObjectURL(response.result);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName || 'adjunto';
    link.click();
    URL.revokeObjectURL(url);
  }

  private async uploadSelectedFiles(supplierId: string | number | null): Promise<{success: boolean; message?: string}> {
    const files = [this.logoFile, ...this.pendingAttachments].filter(Boolean) as File[];
    if (!files.length) {
      return {success: true};
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId || !supplierId) {
      return {success: false, message: 'No se pudo identificar la empresa o el proveedor para subir archivos.'};
    }

    const response = await this.attachmentService.uploadAttachments({
      companyId: String(companyId),
      files,
      relatedToType: 'supplier',
      relatedToId: supplierId,
      skipLoader: true
    });

    if (!response.success) {
      return {success: false, message: response.message};
    }

    this.clearSelectedFiles();
    return {success: true};
  }

  private clearSelectedFiles() {
    this.logoFile = null;
    this.revokeLogoPreview();
    this.pendingAttachments = [];
  }

  private setLogoPreview(file: File | null) {
    this.revokeLogoPreview();
    if (!file) {
      return;
    }

    this.logoPreviewUrl = URL.createObjectURL(file);
  }

  private async loadExistingAttachments(supplierId: string | number) {
    const response = await this.supplierService.getSupplierAttachments(supplierId, {skipLoader: true});
    if (!response.success || !response.result) {
      this.existingAttachments = [];
      return;
    }

    this.existingAttachments = response.result;
    await this.resolveExistingLogo();
  }

  private async resolveExistingLogo() {
    const logoFileName = this.existingLogoFileName;
    if (!logoFileName) {
      return;
    }

    const logoAttachment = this.existingAttachments.find(attachment => attachment.fileName === logoFileName);
    if (!logoAttachment?.id) {
      return;
    }

    const response = await this.attachmentService.downloadAttachment(logoAttachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      return;
    }

    this.revokeLogoPreview();
    this.logoPreviewUrl = URL.createObjectURL(response.result);
  }

  private revokeLogoPreview() {
    if (!this.logoPreviewUrl) return;
    URL.revokeObjectURL(this.logoPreviewUrl);
    this.logoPreviewUrl = '';
  }
}
